/**
 * Cuenta de Twilio: saldo actual (grande), si alcanza para lo que cargaron las marcas (`AvisoCoberturaSaldo`), su
 * evolución, el tipo de cuenta y las alertas de gasto (Usage Triggers) con su estado y último disparo. Con una
 * subcuenta Twilio no informa saldo (F7): se dice, sin serie ni número inventado.
 */

import { Link } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { EstadisticasTwilio, UsageTriggerPublico } from "../../types.estadisticas";
import { ACLARACION_SUBCUENTA, CONSOLA_TWILIO, COLORES, haceTexto } from "../../logic/estadisticas";
import { usd } from "../../logic/formato";
import { fechaHora } from "../../../campana-nueva/utils/format";
import { AvisoConectar, ESTILO_TOOLTIP, RUTA_CONFIG } from "./comun";
import { AvisoCoberturaSaldo } from "./AvisoCoberturaSaldo";

const NOMBRE_TRIGGER: Record<UsageTriggerPublico["clave"], string> = {
  gasto_diario: "Gasto del día",
  gasto_mensual: "Gasto del mes",
  ajeno: "Alerta creada fuera de Deenex",
};

export function ListaTriggers({ triggers }: { triggers: UsageTriggerPublico[] | null | undefined }) {
  const ts = triggers || [];
  if (!ts.length) return <p className="text-sm text-slate-400">No hay alertas de gasto configuradas en Twilio.</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {ts.map((t) => (
        <li key={t.sid || t.nombre} className="py-2 flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm text-slate-800">{NOMBRE_TRIGGER[t.clave] ?? t.nombre}{t.clave === "ajeno" && t.nombre ? ` («${t.nombre}»)` : ""}</p>
            <p className="text-[11px] text-slate-400">
              {t.valor !== null ? `Avisa al pasar ${usd(t.valor)}` : "Sin monto"}
              {t.recurring ? ` · ${t.recurring === "daily" ? "cada día (UTC)" : t.recurring === "monthly" ? "cada mes (UTC)" : t.recurring}` : ""}
            </p>
          </div>
          <p className={`text-xs ${t.disparadoAt ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
            {t.disparadoAt ? `Se disparó el ${fechaHora(t.disparadoAt)}${t.actual !== null ? ` (${usd(t.actual)})` : ""}` : "No se disparó"}
          </p>
        </li>
      ))}
    </ul>
  );
}

export function TarjetaCuentaTwilio({ twilio, ahora = new Date() }: { twilio: EstadisticasTwilio; ahora?: Date }) {
  if (!twilio.disponible) return <AvisoConectar motivo={twilio.motivoNoDisponible} />;
  const s = twilio.saldo;
  const serie = (s?.serie || []).filter((p) => Number.isFinite(Date.parse(p.at)) && Number.isFinite(Number(p.valor)))
    .map((p) => ({ t: Date.parse(p.at), valor: Number(p.valor) }))
    .sort((a, b) => a.t - b.t);
  const tipo = twilio.cuenta
    ? `${twilio.cuenta.tipo === "Trial" ? "Cuenta de prueba (Trial)" : twilio.cuenta.tipo === "Full" ? "Cuenta paga" : twilio.cuenta.tipo || "Tipo sin dato"}${twilio.cuenta.esSubcuenta === true ? " · subcuenta" : twilio.cuenta.esSubcuenta === false ? " · cuenta principal" : ""}`
    : "Sin datos de la cuenta";

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {s?.noAplica ? (
            <>
              <p className="text-sm text-slate-700 max-w-md">{ACLARACION_SUBCUENTA}</p>
              <a href={CONSOLA_TWILIO} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-sm font-semibold text-violet-700 hover:underline">Abrir la consola de Twilio</a>
            </>
          ) : (
            <>
              <p className={`text-[30px] leading-none font-semibold tracking-tight ${s?.valor === null || s?.valor === undefined ? "text-slate-300" : "text-slate-900"}`}>
                {s?.valor === null || s?.valor === undefined ? "—" : usd(s.valor)}
                {s?.moneda && s.moneda.toUpperCase() !== "USD" && <span className="ml-1 text-sm text-slate-500">{s.moneda}</span>}
              </p>
              <p className="mt-1.5 text-[13px] font-medium text-slate-600">Saldo en Twilio</p>
              <p className="text-[11px] text-slate-400">{s?.at ? `Actualizado ${haceTexto(s.at, ahora)}` : "Todavía no se trajo el saldo"}</p>
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">{tipo}{twilio.cuenta?.nombre ? ` · ${twilio.cuenta.nombre}` : ""}</p>
      </div>

      <AvisoCoberturaSaldo cobertura={twilio.coberturaSaldo} saldoMinimoUSD={twilio.alertas?.config?.saldoMinimoUSD} />

      {!s?.noAplica && serie.length > 1 && (
        <div style={{ width: "100%", height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie} margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
              <CartesianGrid stroke={COLORES.grilla} vertical={false} />
              <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false}
                tickFormatter={(v) => new Date(Number(v)).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `$${Number(v).toLocaleString("es-AR")}`} />
              <Tooltip {...ESTILO_TOOLTIP} labelFormatter={(v) => fechaHora(new Date(Number(v)))} formatter={(v) => [usd(Number(v)), "Saldo"]} />
              <Line type="stepAfter" dataKey="valor" name="Saldo" stroke={COLORES.saldo} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-3 mb-1">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Alertas de gasto</h4>
          <Link to={`${RUTA_CONFIG}?seccion=cuenta`} className="text-xs font-semibold text-violet-700 hover:underline">Configurar alertas</Link>
        </div>
        <ListaTriggers triggers={twilio.alertas?.triggers} />
      </div>
    </div>
  );
}

export default TarjetaCuentaTwilio;
